// Auth store — the normalized principal the portal shell gates on. Adapted
// from go-schedule's src/stores/auth.ts for the fsgnext supergraph.
//
// CAPABILITY TRUTH IS SERVER-SIDE (ui-conventions.md #74, stack.md "Auth
// tiers"). `ceilingRoles` are read off the Keycloak token and are only a
// FIRST-FRAME HINT — enough to decide which nav entries to paint before the
// first query answers, and nothing more. What a caller may actually see or do
// is decided by the fsgnext router from the relationship graph, not from
// these claims; `serverViewer` (Query.me, specs/portal-authnz/SPEC.md §5) is
// where the server's answer lands once `syncViewer` has run, and every gate
// below re-checks against it first, falling back to the ceiling hint only
// until that sync completes. The hint is a UX convenience, not a security
// boundary — nothing here is trusted to withhold data, only to avoid
// flashing a control that the server would refuse anyway.
import { defineStore } from 'pinia'
import type Keycloak from 'keycloak-js'
import type { Client } from '@urql/vue'
import * as Sentry from '@sentry/vue'
import { MeDocument } from 'src/core/viewer.operations'
import { cmsFeature } from 'src/core/cmsFeature'
import { ceremonyFeature } from 'src/core/ceremonyFeature'
import { staticsFeedbackFeature } from 'src/core/staticsFeedbackFeature'
import { mapFeature } from 'src/core/mapFeature'
import { announcementFeature } from 'src/core/announcementFeature'
import type { MeQuery } from 'src/graphql/graphql'
import { rememberRealmChoice, type RealmChoice, type RealmConfig } from 'src/core/realm'

/** The typed `Query.me` result — server truth for identity, resolved
 *  capabilities and impersonation state. Null until `syncViewer` has
 *  succeeded at least once (or after it fails — see its own comment). */
export type Viewer = MeQuery['me']

/** Sentry replays/events carry the AUTHENTICATED user (owner ruling
 *  2026-08-24) — under impersonation the REAL subject stays the Sentry
 *  user (the impersonation target rides as a tag), so a replay always
 *  traces to the human at the keyboard. */
function identifySentryUser(me: Viewer): void {
  const impersonating = me.impersonation != null
  const real = me.impersonation?.realPerson ?? me.person
  // me.person is the impersonation TARGET while acting-as — only attach
  // the email when it belongs to the real subject.
  const email = !impersonating ? me.person?.email : null
  Sentry.setUser({
    id: real?.id ?? me.principal.id,
    username: real ? `${real.givenName} ${real.familyName}` : me.principal.displayName,
    ...(email ? { email } : {}),
  })
  Sentry.setTag('impersonating', impersonating ? (me.impersonation?.target?.id ?? 'yes') : null)
}

/** Whoever is signed in, as the token's own claims describe them. Hints only
 *  — see the module comment. */
export interface TokenParsedHint {
  name: string
  email: string
  /** The `sub` claim — the principal's stable Keycloak subject id. */
  subject: string
}

/** Whose view the portal is borrowing, when an admin is checking what a
 *  person is served. */
export interface Impersonation {
  personId: string
  label: string
}

interface AuthState {
  keycloak: Keycloak | null
  authenticated: boolean
  mock: boolean
  /** Which realm this session signed in against (SPEC.md U14) — null on the
   *  pre-auth funnel pages, where no Keycloak instance is constructed at all
   *  (src/boot/keycloak.ts). Display + the "Staff sign-in" switch read it. */
  realm: RealmConfig | null
  /** True on the pre-auth surface: the app mounted deliberately without
   *  attempting authentication, which is NOT the same as "authentication
   *  failed" and must never render as a signed-out error. */
  preAuth: boolean
  token: string | null
  tokenParsed: TokenParsedHint
  /** Client roles on the shared `fsgnext` vocabulary (resource_access.fsgnext.roles)
   *  — distinct from KEYCLOAK_CLIENT, which is this app's own auth client
   *  (fsgnext-portal) and carries no role claims of its own. See module comment. */
  ceilingRoles: string[]
  impersonation: Impersonation | null
  /** The server-evaluated capability answer (Query.me). Null before the
   *  first successful sync, and on every sync failure — see module comment. */
  serverViewer: Viewer | null
  /** The REAL (non-impersonated) principal's Query.me answer, populated only
   *  while impersonation is active — the ui_report surface gates on it
   *  (owner ruling 2026-08-26, impersonation-transparent; see the
   *  `reportViewer` getter). Snapshotted from `serverViewer` when acting-as
   *  starts in-session (`viewAs`), or fetched once WITHOUT the impersonation
   *  header after a reload restores impersonation from sessionStorage
   *  (`syncRealViewer`). Null outside impersonation. */
  realViewer: Viewer | null
  /** True while `syncViewer` has a request in flight — ImpersonationBar's
   *  loading state after an act-as swap reads this. */
  viewerSyncing: boolean
}

/** Core-data write-scope kinds core-admin-crud gates on (ViewerScope.kind,
 *  specs/portal-authnz/SPEC.md §5) — tenant/competition/event/team master
 *  data, the surface `canEditCore` renders for. */
const CORE_SCOPE_KINDS = ['tenant', 'competition', 'event', 'team']

const IMPERSONATION_KEY = 'portal.viewAs'
const EMPTY_HINT: TokenParsedHint = { name: '', email: '', subject: '' }

function restoreImpersonation(): Impersonation | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY)
    return raw ? (JSON.parse(raw) as Impersonation) : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    keycloak: null,
    authenticated: false,
    mock: false,
    realm: null,
    preAuth: false,
    token: null,
    tokenParsed: { ...EMPTY_HINT },
    ceilingRoles: [],
    impersonation: restoreImpersonation(),
    serverViewer: null,
    realViewer: null,
    viewerSyncing: false,
  }),

  getters: {
    /** Headers every GraphQL operation carries while viewing as somebody
     *  else. The server re-derives the whole answer from this rather than
     *  from a client-side approximation of it. */
    impersonationHeaders(s): Record<string, string> {
      if (!s.impersonation) return {}
      return { 'X-Impersonate-Person': s.impersonation.personId }
    },
    // Server truth once `serverViewer` is synced; the ceiling hint (ui-only,
    // see module comment) is the fallback until then. Both compose the same
    // way `manage.roles` did in go-schedule: administering the authorization
    // model is a stronger claim than merely reading widely.
    isAdmin(s): boolean {
      if (s.serverViewer) return s.serverViewer.roles.some((r) => r.name === 'system.all')
      return s.ceilingRoles.includes('admin')
    },
    canImpersonate(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'system.all' || r.name === 'admin.impersonate',
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('impersonate')
    },
    /**
     * The `platform.admin` ceiling, and nothing else — the gate the RBAC
     * module renders behind (specs/archive/rbac/SPEC.md §6, Z0e).
     *
     * Deliberately NOT `system.all || platform.admin`, unlike `canEditCore`
     * above: pkg/authnz's `IsPlatformAdmin` scans the resolved RoleSet for
     * `platform.admin` by name precisely because `Check` would let
     * `system.all` through, and "platform.admin is never implied by admin"
     * (C4) is the rule the whole Rbac surface is gated on. A hint that let a
     * root admin through would paint a module every one of whose operations
     * the server then refuses.
     *
     * Server truth once `serverViewer` is synced — `Authorizer.Roles` turns
     * the FSG ceiling into a role of the same name, so it appears in
     * `me.roles`. The token hint is the fallback until then; under
     * impersonation the viewer carries the TARGET's ceiling (empty by
     * construction), so the module disappears exactly as it should.
     */
    isPlatformAdmin(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) => r.name === 'platform.admin') ||
          s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('platform.admin')
    },
    /** Whether core master-data editing renders (core-admin-crud gate:
     *  platform.admin / system.all, or a write scope on core kinds, with
     *  admin passing everything). Ceiling hint until the Me sync upgrades it
     *  to server truth. */
    canEditCore(s): boolean {
      if (s.serverViewer) {
        const roleHit = s.serverViewer.roles.some(
          (r) => r.name === 'system.all' || r.name.includes('platform.admin'),
        )
        if (roleHit) return true
        return s.serverViewer.scopes.some(
          (scope) => CORE_SCOPE_KINDS.includes(scope.kind) && scope.action === 'write' && scope.all,
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /**
     * Whether the participation add / status-transition / delete controls
     * render (issue #37 §6, ruling P3/P5).
     *
     * A SEPARATE getter from `canEditCore` on purpose. The backend widened
     * participation writes to `competition.admin` — an administrator of their
     * OWN competition, whose core write scope is qualified to those
     * competitions rather than `all` — but it widened NOTHING else:
     * `canEditCore` also gates teams, universities, persons, vehicles and
     * every other core master-data surface, which stayed platform-admin /
     * `Scope(...).All` only. Widening `canEditCore` itself would paint a
     * competition admin edit buttons on all of those, each of which the
     * server still refuses.
     *
     * Server truth once `Query.me` answers — `competition.admin` is a grant
     * role the Keycloak token never carries, so before that sync the only
     * honest hint is `canEditCore`'s own ceiling fallback, and a competition
     * admin simply sees no participation controls for that frame. Paint only:
     * the server re-checks the participation's event's competition against
     * the actor's scope on every one of the three mutations.
     */
    canEditParticipations(s): boolean {
      if (s.serverViewer) {
        const roleHit = s.serverViewer.roles.some(
          (r) =>
            r.name === 'system.all' ||
            r.name.includes('platform.admin') ||
            r.name === 'competition.admin',
        )
        if (roleHit) return true
        return s.serverViewer.scopes.some(
          (scope) => CORE_SCOPE_KINDS.includes(scope.kind) && scope.action === 'write' && scope.all,
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /**
     * The viewer the ui_report surface gates on — the REAL principal's
     * answer while impersonating (owner ruling 2026-08-26, impersonation-
     * TRANSPARENT; server leg: services/core/graph/ui_reports.go
     * `requireUiReport`, commit 1632f269): a tester acting as someone is
     * still themselves the tester, so the report button, the Reports nav
     * and withdraw-ownership follow the real viewer's grants, never the
     * target's. `serverViewer` outside impersonation; `realViewer` while
     * impersonating — which is null until its snapshot/restore fetch lands,
     * leaving each gate to its own fallback. Every OTHER surface keeps true
     * act-as semantics and stays on `serverViewer`.
     */
    reportViewer(s): Viewer | null {
      return s.impersonation ? s.realViewer : s.serverViewer
    },
    /**
     * The Deadlines nav entry gate (ui-reports 01a03bc7/01a03bc9): the
     * module serves reviewers holding a `deadline.*` grant role
     * (deadline.read.status / deadline.read.document /
     * deadline.review.document, pkg/authnz grantRules) as well as admins.
     * Grant roles are RBAC data the token never carries, so this is server
     * truth once `Query.me` answers; the `admin` ceiling is the only honest
     * hint before that. Paint only — every operation stays server-checked.
     */
    canSeeDeadlines(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'system.all' || r.name.startsWith('deadline.'),
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The Hotel nav entry gate (ui-report 01a03bc9): `hotel.manage` gates
     * every hotel surface server-side (hotel SPEC §6), so its holders get
     * the entry painted too. Same server-truth/ceiling split as above.
     */
    canSeeHotel(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'system.all' || r.name === 'hotel.manage',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The press accreditation queue (j55 HT-11): `accred.review` is the
     * competition-scoped grant role behind memberReviewAccreditation and
     * memberUpdatePressAccreditation's reviewer path (pkg/authnz grantRules).
     * Grant roles are RBAC data the token never carries; server truth once
     * `Query.me` answers, the `admin` ceiling the only honest hint before.
     * Paint only — every review mutation stays server-checked per event.
     */
    canReviewAccreditations(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'accred.review' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The person panel's qualification review controls (ui-report 01a040a9):
     * `qual.review` is the member subgraph's grant role for
     * memberReviewQualification (pkg/authnz grantRules — person-scoped), so
     * its holders get the verdict/comment surface painted alongside admins.
     * Grant roles are RBAC data the token never carries; server truth once
     * `Query.me` answers, the `admin` ceiling the only honest hint before.
     * Paint only — the mutation stays server-checked (and PII types
     * additionally require read.pii:hic server-side, whose absence already
     * nulls the revision document this surface hangs off).
     */
    canReviewQualifications(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some((r) => r.name === 'qual.review' || r.name === 'system.all')
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The person panel's "Review without document" action (issue #36, admin
     * attestation via memberReviewQualificationRecord). Server gate
     * (services/core/graph/member_helpers.go requireEventAdminForPerson): the
     * platform.admin ceiling, or competition.admin projected through the
     * person's competitions — system.all passes the latter. Deliberately NOT
     * qual.review: the attestation gate is narrower than ordinary review.
     * Server truth once `Query.me` answers; the admin / platform.admin ceiling
     * is the hint before. Paint only — the server re-checks the person's
     * competition, and PII types additionally need read.pii:hic.
     */
    canAttestQualifications(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some(
            (r) =>
              r.name === 'system.all' ||
              r.name === 'platform.admin' ||
              r.name === 'competition.admin',
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /**
     * Whether the shell's "Report a problem" button renders
     * (specs/archive/ui-reports/SPEC.md §Portal work package): `portal.reporter`
     * ONLY — filing is reporter-only server-side, and `portal.triage` /
     * `platform.admin` deliberately cannot file (R6), so painting the button
     * for them would be a control the server refuses.
     *
     * SERVER TRUTH ONLY, no ceiling fallback: portal.reporter is an
     * RBAC-GROUP role, which the Keycloak token never carries — before
     * `Query.me` answers there is nothing honest to hint from, so the button
     * is simply absent for a frame.
     *
     * Under impersonation this reads the REAL principal's viewer (owner
     * ruling 2026-08-26, transparent surface — see `reportViewer`): the
     * button must not vanish because the target lacks portal.reporter, and
     * a role-less impersonator gains nothing from a reporter target.
     */
    canReportProblem(): boolean {
      return this.reportViewer?.roles.some((r) => r.name === 'portal.reporter') ?? false
    },
    /**
     * The Reports nav entry / view gate (module brief: `portal.reporter` OR
     * `portal.triage` OR `platform.admin` may read the pool). The two grant
     * roles exist only server-side; `platform.admin` alone has a token
     * ceiling to hint from before the Me sync lands.
     *
     * Under impersonation this reads the REAL principal's viewer (owner
     * ruling 2026-08-26, transparent surface — see `reportViewer`). The
     * ceiling-hint fallback stays honest there too: `s.ceilingRoles` comes
     * off the Keycloak token, which never changes under impersonation, so
     * it is always the REAL session's ceiling.
     */
    canSeeReports(s): boolean {
      const viewer = this.reportViewer
      if (viewer) {
        return (
          viewer.roles.some(
            (r) =>
              r.name === 'portal.reporter' ||
              r.name === 'portal.triage' ||
              r.name === 'platform.admin',
          ) || viewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('platform.admin')
    },
    /**
     * The tickets module's nav/view gate (services/portal/docs/modules/
     * tickets.md): any competition-scoped `ticket.*` grant role, or
     * `system.all`. The grant roles are RBAC data the token never carries,
     * so before the Me sync only the admin ceiling can honestly hint.
     */
    canSeeTickets(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name.startsWith('ticket.') || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /** Sidebar editing / create / delete (T9: ticket.issue and above). */
    canIssueTickets(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some((r) =>
          ['ticket.issue', 'ticket.print', 'ticket.admin', 'system.all'].includes(r.name),
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /** The desk print actions (T9: ticket.print and above). */
    canPrintTickets(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some((r) =>
          ['ticket.print', 'ticket.admin', 'system.all'].includes(r.name),
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The cross-service audit viewer (services/portal/docs/modules/audit.md).
     * Mirrors the server gate exactly: core's auditAdminAllowed accepts the
     * `admin`, `read.all` or `platform.admin` CEILING (not a grant role, so
     * `system.all` alone does not pass). Server truth once `Query.me` answers
     * (its `ceilingRoles`), the token hint before. Paint only.
     */
    /** The viewer's ACTIVE team membership (Query.me), or null — the team
     *  pages' anchor (redesign batch 6). Server truth only: the token carries
     *  no team, so there is no pre-sync hint. */
    myTeamMembership(s) {
      return s.serverViewer?.person?.membership ?? null
    },
    /** Any active team membership — the "My team" rail entry. */
    hasTeam(s): boolean {
      return !!s.serverViewer?.person?.membership
    },
    /** A captain of the viewer's team: the `team.captain` relation that
     *  authorizes the captain actions (set roles, delegate capabilities —
     *  pkg/authnz/policy.go roleTeamCaptain). First-frame hint only. */
    isTeamCaptain(s): boolean {
      return s.serverViewer?.person?.membership?.role === 'CAPTAIN'
    },
    /** The account's standing in a few words — under the name in the rail's
     *  foot, the account menu and Account (prototype `u.role`). Most
     *  specific first. */
    accountStanding(): string {
      if (this.isPlatformAdmin) return 'Platform admin'
      if (this.isAdmin) return 'Admin'
      const team = this.myTeamMembership?.team.name
      if (team) return `${this.isTeamCaptain ? 'Captain' : 'Team member'} · ${team}`
      return this.realm?.label ?? 'FSG account'
    },
    canReadAudit(s): boolean {
      const ceiling = s.serverViewer ? s.serverViewer.ceilingRoles : s.ceilingRoles
      return ['admin', 'read.all', 'platform.admin'].some((r) => ceiling.includes(r))
    },
    /** Bulk Print / Ticket Types / Audit Log (T9: ticket.admin only). */
    canAdminTickets(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some((r) => ['ticket.admin', 'system.all'].includes(r.name))
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The competitions the viewer's ticket.* roles are scoped to — the
     * tickets module's DEFAULT event scope when no global scope is set
     * (module brief: "current-season events of the competitions the caller
     * holds any ticket.* role for"). Empty means unscoped (an unqualified
     * role or system.all): every current-season event.
     */
    ticketCompetitionIds(s): string[] {
      if (!s.serverViewer) return []
      const ids = new Set<string>()
      for (const r of s.serverViewer.roles) {
        if (!r.name.startsWith('ticket.')) continue
        if (!r.competition) return []
        ids.add(r.competition.id)
      }
      return [...ids]
    },
    /**
     * The Expenses nav entry (services/portal/docs/modules/expenses.md, G26).
     *
     * The BRIEF's gate is `finance.expenses.submit` — and that role gates
     * nothing else anywhere: the finance SDL says so in as many words
     * ("finance.expenses.submit gates NOTHING here, it only gates the portal
     * nav link"), and pkg/authnz registers it with an EMPTY rule set
     * (policy.go, proven by finance_policy_test.go).
     *
     * The two review/admin roles ride along DELIBERATELY. The rail renders a
     * module's children only while the PARENT entry is visible
     * (MainLayout.vue), so a reviewer holding `finance.expenses.review` but
     * not `submit` would have no way to reach the "Review" sub-item at all.
     * Painting the parent for them costs nothing — the module is reachable at
     * its named route regardless (G26: no link, no redirect) and every
     * operation under it is server-enforced.
     *
     * Grant roles are RBAC data the Keycloak token never carries, so this is
     * server truth once Query.me answers; the `admin` ceiling is the only
     * honest hint before that.
     */
    canSubmitExpenses(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) =>
            r.name === 'finance.expenses.submit' ||
            r.name === 'finance.expenses.review' ||
            r.name === 'finance.admin' ||
            r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The reviewer surfaces: the queue, the transition buttons, the EPC QR,
     * the payout batches and the accounting export (module brief §Actors).
     *
     * `finance.admin` is deliberately NOT included. It reads everything and
     * writes nothing in this sub-domain — pkg/authnz's finance_policy_test
     * pins exactly that ("finance.admin must NOT write expenses (G25
     * four-eyes)") — so painting reviewer controls for a finance admin would
     * be painting controls the server refuses.
     */
    canReviewExpenses(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'finance.expenses.review' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The finance-admin module (services/portal/docs/modules/finance-admin.md):
     * orders, invoices, credit notes, refunds — `finance.admin` exclusively.
     * Same server-truth/ceiling split as the two above.
     */
    canAdminFinance(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'finance.admin' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The Rules nav entry / explorer gate (services/portal/docs/modules/
     * rules.md; fsgadmin/fsgnext#68 B13): every signed-in user reads the
     * rules plane (owner ruling 2026-11-01, superseding B23's grant step);
     * the anonymous public token never does.
     *
     * Paint only, as always: the /rules route stays reachable at its named
     * route with no guard, and a refusal surfaces as the screen's own error
     * banner (#34).
     */
    canReadRules(s): boolean {
      return s.authenticated
    },
    /** Navigation hints only; tracker actions use the server's per-ticket Caps. */
    canUseRulesTracker(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some((r) =>
          [
            'team.member',
            'team.captain',
            'rules.official',
            'rules.review.junior',
            'rules.review',
            'system.all',
          ].includes(r.name),
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    canReviewRules(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some((r) =>
          ['rules.review.junior', 'rules.review', 'system.all'].includes(r.name),
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The docs module gate (services/portal/docs/modules/docs.md;
     * fsgadmin/fsgnext#69, register M, M13/M20): the `docs.public.manage`
     * grant role — ONE role for both the docstore upload/replace/move/delete
     * plane and core's publication (publish/unpublish/reorder) plane (M20) —
     * or `system.all`.
     *
     * `docs.public.manage` is ANCHOR-FREE (the public-website documents carry
     * no competition of their own beyond a fixed owner constant) and RBAC
     * data the Keycloak token never carries, so this is server truth once
     * `Query.me` answers. The `admin` ceiling is the only honest hint before
     * that. Held narrowly at launch (M13: the PR/rules-committee circle who
     * edit the public site today) — most accounts never see this entry, by
     * design, not as a rollout gap.
     */
    canManageDocs(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'docs.public.manage' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The CMS inline editor (#160 AF13, WP8a): editing drafts is `cms.edit`
     * — or a higher cms role (`cms.publish`, `cms.admin`), as the cms
     * service's own gate counts them (services/cms/internal/authx CanEdit) —
     * or a platform-admin ceiling (`system.all`, `platform.admin`).
     * Platform-scoped grant roles are RBAC data the token never carries, so
     * this is server truth once `Query.me` answers; before that the `admin`
     * ceiling is the only honest hint. The server refuses every editor call
     * anyway; this only decides whether "Edit page" is drawn.
     */
    canEditCms(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['cms.edit', 'cms.publish', 'cms.admin', 'system.all', 'platform.admin'].includes(
              r.name,
            ),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** Publish, restore (and delete) pages: `cms.publish` or `cms.admin`
     *  (authx CanPublish), or a platform-admin ceiling. */
    canPublishCms(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['cms.publish', 'cms.admin', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** The cms administration (#160 AF13): audiences, the page tree's
     *  structure (create, move, paths, menu settings), redirects and the
     *  audit log — `cms.admin` (authx IsAdmin), or a platform-admin ceiling. */
    canAdminCms(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['cms.admin', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** The Website tool's rail entry (#160 WP8b): a cms editor, while the
     *  tenant's `cms` feature is not known to be off (AF20,
     *  src/core/cmsFeature.ts). */
    canUseWebsiteTool(): boolean {
      return this.canEditCms && !cmsFeature.off
    },
    /**
     * The News module's editing (#162 AH9): one platform-scoped grant role,
     * `news.manage` — write, publish, schedule, unpublish, delete, and the
     * categories — held by admins and the communications volunteers; a
     * platform-admin ceiling (`system.all`, `platform.admin`) passes too.
     * Server truth once `Query.me` answers; before that the `admin` ceiling is
     * the only honest hint. The cms service refuses every News write anyway.
     */
    canManageNews(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['news.manage', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** The News tool's rail entry: news.manage while the tenant's `cms`
     *  feature is not known to be off (News follows the cms flag, AH11). */
    canUseNewsTool(): boolean {
      return this.canManageNews && !cmsFeature.off
    },
    /**
     * The Award ceremony tool (#163 AI17): `ceremony.read` or
     * `ceremony.edit` (competition-scoped grant roles the token never
     * carries), `system.all`, or a platform-admin ceiling — the theme uploads,
     * the program import and the audit log are platform-admin surfaces of
     * this same tool. Server truth once `Query.me` answers; the `admin`
     * ceiling is the only honest hint before that. Paint only: every ceremony
     * root re-checks server-side against the season's competition.
     */
    canReadCeremony(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['ceremony.read', 'ceremony.edit', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** Ceremonies, the program, overrides, photos, builds and the final
     *  marker (#163 AI17): `ceremony.edit` or `system.all`. */
    canEditCeremony(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'ceremony.edit' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /** The tool's rail entry: a ceremony reader while the tenant's
     *  `ceremony` feature is not known to be off (AI19,
     *  src/core/ceremonyFeature.ts). */
    canUseCeremonyTool(): boolean {
      return this.canReadCeremony && !ceremonyFeature.off
    },
    /**
     * Statics feedback booking's officials' side (#165 AK9): `statics-feedback.manage`
     * (a competition-scoped grant role, optionally qualified to statics feedback
     * types — the token never carries it), `system.all`, or a platform-admin
     * ceiling. Server truth once `Query.me` answers; the `admin` ceiling is
     * the only honest hint before that. Paint only: every statics feedback write
     * re-checks server-side against the type's event and qualifier.
     */
    canManageStaticsFeedback(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['statics-feedback.manage', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** The Statics feedback tool's rail entry: a statics feedback manager while the tenant's
     *  `statics-feedback` feature is not known to be off (AK12,
     *  src/core/staticsFeedbackFeature.ts). */
    canUseStaticsFeedbackTool(): boolean {
      return this.canManageStaticsFeedback && !staticsFeedbackFeature.off
    },
    /** My team › Statics feedback: any team member (AK2 — all members read the
     *  team's bookings) while the `statics-feedback` feature is not known to be off. */
    canUseTeamStaticsFeedback(): boolean {
      return this.hasTeam && !staticsFeedbackFeature.off
    },
    /** The event map (#169): every viewer while the tenant's `map` feature is
     *  not known to be off (src/core/mapFeature.ts) — which layers they see
     *  is the server's decision (AO5). */
    canUseEventMap(): boolean {
      return !mapFeature.off
    },
    /**
     * The map editor (#169 WP8a, `/tools/map`): the `map.edit` grant role
     * (competition-scoped in pkg/authnz — the token never carries it),
     * `system.all`, or a platform-admin ceiling. Server truth once `Query.me`
     * answers; the `admin`/`platform.admin` ceilings are the only honest hint
     * before that. Paint only: every map mutation re-checks `map.edit` against
     * the map's competition.
     */
    canEditMap(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['map.edit', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** The map editor's rail entry: a map editor while the tenant's `map`
     *  feature is not known to be off. */
    canUseMapEditor(): boolean {
      return this.canEditMap && !mapFeature.off
    },
    /**
     * Announcements (#164): the `announcement.publish` grant role
     * (competition-scoped — the token never carries it), `system.all`, or a
     * platform-admin ceiling. Server truth once `Query.me` answers; the
     * `admin`/`platform.admin` ceilings are the only honest hint before that.
     * Paint only: every announcement field re-checks server-side.
     */
    canPublishAnnouncements(s): boolean {
      if (s.serverViewer) {
        return (
          s.serverViewer.roles.some((r) =>
            ['announcement.publish', 'system.all', 'platform.admin'].includes(r.name),
          ) || s.serverViewer.ceilingRoles.includes('platform.admin')
        )
      }
      return s.ceilingRoles.includes('admin') || s.ceilingRoles.includes('platform.admin')
    },
    /** The Announcements tool's rail entry: an announcer AND the tenant's
     *  `announcements` feature positively known to be on (dark until cutover:
     *  unknown counts as off, src/core/announcementFeature.ts). */
    canUseAnnouncementTool(): boolean {
      return this.canPublishAnnouncements && announcementFeature.on
    },
    /**
     * The notification admin plane (services/portal/docs/modules/
     * notification-admin.md, NO-4): the mail log, test-send, retry, senders
     * and env policies — every one `requireAdmin` in notification-svc, i.e.
     * the `notification.admin` grant role or `system.all`. RBAC data the
     * token never carries, so server truth once `Query.me` answers, with the
     * `admin` ceiling as the only honest pre-sync hint.
     */
    canAdminNotifications(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'notification.admin' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The sponsors admin gate (services/portal/docs/modules/sponsors.md):
     * the `sponsor.manage` grant role (tenant-scoped, minted on one of the
     * organizer's competitions) or `system.all`. RBAC data the token never
     * carries, so server truth once `Query.me` answers; the `admin` ceiling is
     * the only honest hint before that.
     */
    canManageSponsors(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name === 'sponsor.manage' || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * The Scoring nav entry / module gate (services/portal/docs/modules/
     * scoring.md; fsgadmin/fsgnext#85 WP10): any `scoring.*` grant role, or
     * `system.all`.
     *
     * A PREFIX match rather than an enumeration, and the tickets getter's
     * shape for the same reason: pkg/authnz/policy.go registers seven scoring
     * grant roles today (read, read.results, input.write, penalty.store,
     * penalty.case, publish, admin) and the register is explicitly the home
     * of the tier vocabulary — an eighth added there must open the module
     * too, not wait for this list to be updated.
     *
     * The roles are competition-scoped RBAC data the Keycloak token never
     * carries, so this is server truth once `Query.me` answers; the `admin`
     * ceiling is the only honest hint before that. Paint only — every scoring
     * root re-checks server-side, and an authenticated caller holding NO
     * scoring grant is refused the whole plane (module brief, open question 1).
     */
    canSeeScoring(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) => r.name.startsWith('scoring.') || r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * Everything in the scoring module ABOVE the results tier — the input
     * desk, the WAL history, the penalty workflow, the running order, awards,
     * traces, configuration and the audit ledgers (module brief §Actors &
     * capabilities).
     *
     * A separate getter from `canSeeScoring` because `scoring.read.results` is
     * a genuinely narrower READ tier, not merely a weaker write tier: the
     * scoring resolvers clamp it to results, standings and publications and
     * refuse it runs, statics, the WAL, traces, draft cases and the producer
     * audit. policy.go says why the clamp is code rather than policy data
     * ("Resource carries no tier field, so the policy table cannot express
     * it"), which is exactly why the client has to mirror the tier by name.
     *
     * So the nav mirrors the server honestly: a results-tier official sees the
     * Scoring entry with Results and Publications under it, and is not offered
     * eight links whose every query would error. Same server-truth/ceiling
     * split as above; paint only.
     */
    canReadScoringFull(s): boolean {
      if (s.serverViewer) {
        return s.serverViewer.roles.some(
          (r) =>
            (r.name.startsWith('scoring.') && r.name !== 'scoring.read.results') ||
            r.name === 'system.all',
        )
      }
      return s.ceilingRoles.includes('admin')
    },
    /**
     * An authenticated account that no Person is linked to (SPEC.md §8's
     * unlinked banner).
     *
     * Only true once the SERVER has answered: `serverViewer` null means "we do
     * not know yet", and guessing "unlinked" there would flash a claim CTA at
     * everyone on every cold load. Community accounts self-heal their link on
     * the `person_id` claim (§6), so this state is genuinely rare and always
     * actionable — the person has an account but nothing in the estate to
     * attach it to yet.
     */
    isUnlinked(s): boolean {
      return s.serverViewer !== null && s.serverViewer.person === null
    },
  },

  actions: {
    /** Offline/mock dev only — a stub principal, no real issuer. */
    setMockPrincipal() {
      this.mock = true
      this.authenticated = true
      this.token = null
      this.tokenParsed = { name: 'Mock Admin', email: 'mock.admin@fsg.one', subject: 'mock-admin' }
      // platform.admin rides along explicitly — it is never implied by
      // `admin` (see isPlatformAdmin), so without it the RBAC module would be
      // unreachable in mock mode, which is the mode `quasar dev` defaults to.
      this.ceilingRoles = ['admin', 'impersonate', 'platform.admin']
    },

    /** The pre-auth funnel surface — mounted on purpose without a session. */
    setPreAuth() {
      this.preAuth = true
      this.authenticated = false
      this.mock = false
      this.token = null
    },

    setKeycloak(kc: Keycloak, authenticated: boolean, realm: RealmConfig | null = null) {
      this.keycloak = kc
      this.authenticated = authenticated
      this.mock = false
      this.preAuth = false
      this.realm = realm
      this.setToken(kc.token ?? null)
    },

    /**
     * Sign in against the other realm.
     *
     * A realm switch is a whole new identity — a different issuer, a different
     * subject, a different token — so it cannot be done in place: the choice is
     * persisted and the document is reloaded, which sends src/boot/keycloak.ts
     * down its normal `login-required` path against the new realm. Signing out
     * of the old one first is what stops Keycloak from silently re-issuing the
     * previous session on the way past.
     */
    switchRealm(choice: RealmChoice, returnTo = '/my') {
      rememberRealmChoice(choice)
      const target = new URL(returnTo, window.location.origin).toString()
      if (this.keycloak && this.authenticated) {
        this.keycloak.logout({ redirectUri: target })
        return
      }
      window.location.assign(target)
    },

    /**
     * Read the token the Keycloak instance is currently holding and
     * re-derive the hint fields from it. Called on init and on every
     * `updateToken` refresh (src/boot/keycloak.ts).
     */
    setToken(token: string | null) {
      this.token = token
      const parsed = this.keycloak?.tokenParsed as
        | {
            name?: string
            email?: string
            sub?: string
            resource_access?: Record<string, { roles: string[] }>
          }
        | undefined
      if (!parsed) {
        this.tokenParsed = { ...EMPTY_HINT }
        this.ceilingRoles = []
        return
      }
      this.tokenParsed = {
        name: parsed.name ?? '',
        email: parsed.email ?? '',
        subject: parsed.sub ?? '',
      }
      // The shared 'fsgnext' vocabulary, not this app's own auth client — see
      // the ceilingRoles field comment.
      this.ceilingRoles = parsed.resource_access?.fsgnext?.roles ?? []
    },

    /**
     * View the portal as a given person, or stop doing so.
     *
     * Only the header changes; the server re-derives everything from it, so
     * what appears is what that person would really be served. The caller is
     * responsible for calling `resetGraphqlClient()` afterwards — this store
     * has no reference to the urql client, and a partial cache discard is the
     * one failure mode that matters here (ui-conventions.md #78–82).
     */
    viewAs(personId: string, label: string) {
      // Snapshot the REAL principal's answer before the client swap resyncs
      // serverViewer to the target (owner ruling 2026-08-26 — see
      // `realViewer`/`reportViewer`). Only on ENTRY: while already
      // impersonating, serverViewer is the previous target's answer, not a
      // real snapshot worth keeping.
      if (!this.impersonation) this.realViewer = this.serverViewer
      this.impersonation = { personId, label }
      // sessionStorage, not localStorage: closing the tab ends the
      // impersonation, which is the right lifetime for a thing you are
      // checking rather than living in.
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(this.impersonation))
    },

    stopImpersonation() {
      this.impersonation = null
      this.realViewer = null
      sessionStorage.removeItem(IMPERSONATION_KEY)
    },

    /**
     * Fetch `Query.me` and store the server-evaluated capability surface.
     *
     * Network-only: this is the moment the ceiling hint gets replaced by
     * server truth, so a stale cache entry defeats the point. Called by
     * MainLayout after auth completes and after every `resetGraphqlClient()`
     * (impersonation enter/exit) — never from here, since this store has no
     * reference to the urql client (mirrors coreData.ts's own reasoning; see
     * that file's URQL_CLIENT_KEY comment for why boot/urql.ts isn't
     * imported directly).
     *
     * Conservative on failure (go-schedule's syncGrants precedent): clear
     * `serverViewer` rather than keep a possibly-stale answer, so getters
     * fall back to the ceiling hint instead of trusting a snapshot that may
     * no longer hold.
     */
    async syncViewer(client: Client): Promise<void> {
      this.viewerSyncing = true
      try {
        const result = await client
          .query(MeDocument, {}, { requestPolicy: 'network-only' })
          .toPromise()
        if (result.error || !result.data) {
          console.warn('auth: Query.me failed, falling back to ceiling-role hints', result.error)
          this.serverViewer = null
          return
        }
        this.serverViewer = result.data.me
        identifySentryUser(result.data.me)
      } catch (e) {
        console.warn('auth: Query.me failed, falling back to ceiling-role hints', e)
        this.serverViewer = null
      } finally {
        this.viewerSyncing = false
      }
    },

    /**
     * Fetch the REAL principal's `Query.me` while impersonation is active —
     * `realViewer`'s restore path (owner ruling 2026-08-26). After a reload
     * revives impersonation from sessionStorage there is no in-session
     * snapshot to take, so MainLayout calls this ONCE with a bare client
     * (src/boot/urql.ts `createBareClient`) that reuses the bearer but
     * never sends X-Impersonate-Person — its own client, so the real
     * answer cannot land in the swapped main client's document cache.
     *
     * Conservative on failure, like `syncViewer`: a null `realViewer`
     * keeps the report gates closed rather than trusting a stale answer.
     */
    async syncRealViewer(client: Client): Promise<void> {
      try {
        const result = await client
          .query(MeDocument, {}, { requestPolicy: 'network-only' })
          .toPromise()
        if (result.error || !result.data) {
          console.warn(
            'auth: real-principal Query.me failed; report gates stay closed',
            result.error,
          )
          this.realViewer = null
          return
        }
        this.realViewer = result.data.me
      } catch (e) {
        console.warn('auth: real-principal Query.me failed; report gates stay closed', e)
        this.realViewer = null
      }
    },

    logout() {
      this.keycloak?.logout()
    },
  },
})
